import { useEffect, useRef } from 'react';

const team = [
  {
    name: 'Sarthak Bobade',
    role: 'Data Scientist | Environmental Analytics',
    tag: 'Project Lead',
    tagColor: 'bg-[#EDE0D4] text-[#1B4332]',
    desc: 'Led and executed data collection, analysis, machine learning modeling, documentary creation, and end-to-end platform development.',
    skills: ['Data Science', 'Machine Learning', 'Environmental Analytics', 'Documentary'],
    img: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/5819f30f-bdf0-41ab-86de-f894880c43b1_Untitled-design.png?v=3f171ee147a692cade287c6b7a8c5254',
    linkedin: 'https://www.linkedin.com/in/sarthak-bobade-2029ya/',
  },
  {
    name: 'Pavan Khandare',
    role: 'Lead Data Scientist | AI Architect',
    tag: 'Mentor',
    tagColor: 'bg-emerald-100 text-emerald-700',
    desc: 'Provided guidance in machine learning, system design, and real-world implementation of data-driven solutions for environmental analysis. Supported project architecture and practical deployment decisions.',
    skills: ['AI & ML', 'Data Strategy', 'Mentorship', 'Environmental Impact'],
    img: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/e5e6c30d-5e14-4488-91a7-80b8451fb6c5_1738594678957.jpg?v=123f28d526cd430994a0735bd9337553',
    linkedin: 'https://www.linkedin.com/in/pavankhandare/',
  },
  {
    name: 'Dr. Rachana Chavan',
    role: 'Research Advisor | Environmental Science',
    tag: 'Mentor',
    tagColor: 'bg-[#FDEBD0] text-[#A0522D]',
    desc: 'Provided academic and research guidance in environmental science, ensuring methodological accuracy and scientific rigor. Supported data validation, research design, and interpretation of pollution insights.',
    skills: ['Environmental Research', 'Data Validation', 'Research Methodology', 'Scientific Analysis', 'Sustainability'],
    img: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/48f7ce19-9a41-4f41-abbb-b84f648a45a2_Rachna.jpg?v=6e461e7152db03d31ad1c93a8dc6f7c4',
    linkedin: '',
    isAdvisor: true,
  },
];

export default function AboutTeam() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.team-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" className="py-24 md:py-32 bg-gradient-to-b from-[#EDE0D4]/40 to-[#F8F5F0]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 team-animate opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#2A9D8F] text-sm uppercase tracking-[0.25em] font-semibold mb-3">The Team</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The People Behind the Project
          </h2>
          <p className="text-[#6C757D] text-base max-w-xl mx-auto">
            A dedicated team combining data science expertise with a passion for environmental impact.
          </p>
        </div>

        {/* Team cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {team.map((member) => (
            <div
              key={member.name}
              className={`team-animate opacity-0 translate-y-8 transition-all duration-700 rounded-2xl overflow-hidden hover:-translate-y-1 flex flex-col ${
                (member as typeof member & { isAdvisor?: boolean }).isAdvisor
                  ? 'bg-[#FEF9F4] border-2 border-[#F4A261]/40'
                  : 'bg-[#F8F5F0] border border-[#EDE0D4]'
              }`}
              style={(member as typeof member & { isAdvisor?: boolean }).isAdvisor ? { boxShadow: '0 0 18px 2px rgba(244,162,97,0.13)' } : {}}
            >
              {/* Top image area */}
              <div className="relative bg-gradient-to-br from-[#EDE0D4] via-[#F8F5F0] to-[#EDE0D4]/60 p-8 flex flex-col items-center">
                <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-[#F8F5F0]">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className={`mt-4 px-3 py-1 rounded-full text-xs font-semibold ${member.tagColor}`}>
                  {member.tag}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <h3
                  className="text-[#1C1C1C] font-bold text-xl mb-1 text-center"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {member.name}
                </h3>
                <p className="text-[#2A9D8F] text-sm text-center mb-4">{member.role}</p>

                <p className="text-[#6C757D] text-sm leading-relaxed text-center mb-5">{member.desc}</p>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 justify-center flex-1 content-start mb-5">
                  {member.skills.map((skill) => (
                    <span
                      key={skill}
                      className="bg-[#EDE0D4] text-[#6B4F3A] text-xs px-3 py-1 rounded-full h-fit"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* LinkedIn button */}
                <div className="flex justify-center mt-auto pt-4 border-t border-[#EDE0D4]">
                  {member.linkedin ? (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-md border border-[#2A9D8F]/30 bg-[#EDE0D4] text-[#1B4332] text-sm font-medium hover:bg-[#EDE0D4]/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-linkedin-fill text-base" />
                      View Profile on LinkedIn
                    </a>
                  ) : (
                    <div className="h-9" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Final line */}
        <div className="team-animate opacity-0 translate-y-8 transition-all duration-700 mt-16 text-center">
          <div className="inline-block">
            <div className="w-12 h-0.5 bg-[#2A9D8F]/40 mx-auto mb-5" />
            <p
              className="text-[#1C1C1C] text-xl md:text-2xl font-semibold italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              This is Part 1 of the journey — understanding the reality.
            </p>
            <div className="w-12 h-0.5 bg-[#2A9D8F]/40 mx-auto mt-5" />
          </div>
        </div>
      </div>
    </section>
  );
}
